import { useEffect, useState } from "react"
import styled from "styled-components"

import {
  useLazyGetIntelligenceReportingPeriodQuery,
  useLazyGetIntelligenceReportsQuery,
} from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { parseDateField } from "utils/dateUtils"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"

import {
  IntelligenceReportDrawer,
  type IntelligenceReportDrawerRow,
} from "../../IntelligenceReportsPage/components/IntelligenceReportDrawer"
import { IntelligenceReportsEmptyState } from "../../IntelligenceReportsPage/components/IntelligenceReportsEmptyState"
import { IntelligenceReportsTable } from "../../IntelligenceReportsPage/components/IntelligenceReportsTable"
import { convertIntelligenceReportsToTableRows } from "../../IntelligenceReportsPage/convertIntelligenceReportsToTableRows"
import { useCustomerDashboard } from "../CustomerDashboardContext"
import customerDashboardReportsTableHeaderCells from "./customerDashboardReportsTableHeaderCells"

const ReportsTableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const PaginationWrapper = styled.div<{ $sidebarOpen: boolean }>`
  margin-top: auto;
  padding-right: ${({ $sidebarOpen }) => ($sidebarOpen ? "24px" : "0")};
`

export const CustomerDashboardReports = () => {
  const { isSidebarOpen, devices, currentDevice } = useCustomerDashboard()
  const selectedDeviceSn = devices[currentDevice]?.sn
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasIntelligenceReportsAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.IntelligenceReports)
    : false

  const [drawerReport, setDrawerReport] = useState<IntelligenceReportDrawerRow | null>(null)
  const { urlQueryParams } = useUrlQueryParams()

  const [fetchReportingPeriod, { data: periodData }] = useLazyGetIntelligenceReportingPeriodQuery()
  const [fetchReports, { data: reportsData, isFetching, isUninitialized }] = useLazyGetIntelligenceReportsQuery()

  useEffect(() => {
    if (!hasIntelligenceReportsAccess) return
    fetchReportingPeriod()
  }, [hasIntelligenceReportsAccess, fetchReportingPeriod])

  useEffect(() => {
    if (!hasIntelligenceReportsAccess) return
    if (!periodData) return
    fetchReports({
      ...urlQueryParams,
      ...(selectedDeviceSn ? { sn: selectedDeviceSn } : {}),
      start_ts_utc: periodData.data.start_ts_utc,
      end_ts_utc: periodData.data.end_ts_utc,
    } as UITypes.Params)
  }, [urlQueryParams, hasIntelligenceReportsAccess, fetchReports, selectedDeviceSn, periodData])

  const tableData = reportsData
    ? convertIntelligenceReportsToTableRows(reportsData.data).map((row, i) => {
        const report = reportsData.data[i]
        return {
          ...row,
          reporting_period: {
            value: `${parseDateField(report.start_date)} - ${parseDateField(report.end_date)}`,
          },
        }
      })
    : []
  const meta = reportsData?.meta

  const hasSearchFilters = !!(urlQueryParams.sn || urlQueryParams.customer_name || urlQueryParams.customer_email)
  const showEmptyState = !isUninitialized && !isFetching && tableData.length === 0 && !hasSearchFilters

  return (
    <>
      <IntelligenceReportDrawer report={drawerReport} onClose={() => setDrawerReport(null)} />
      {!hasIntelligenceReportsAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.IntelligenceReports} />}
      <PageLayout>
        <ContentWrapper title="Reports" offsetHeight="234px">
          {showEmptyState ? (
            <IntelligenceReportsEmptyState priority="" />
          ) : (
            <ReportsTableWrapper>
              <IntelligenceReportsTable
                data={tableData}
                isLoading={isFetching}
                onViewReport={setDrawerReport}
                headerCells={customerDashboardReportsTableHeaderCells}
              />
            </ReportsTableWrapper>
          )}
        </ContentWrapper>
        <PaginationWrapper $sidebarOpen={isSidebarOpen}>
          <Pagination meta={meta} />
        </PaginationWrapper>
      </PageLayout>
    </>
  )
}
